/* eslint-disable no-underscore-dangle */
/*
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
*/

import type {
  ViewDowncastWriter, ModelElement, LocaleTranslate, ViewPosition, ViewElement,
} from 'ckeditor5';
import { getUpdatedContentBlock } from './bulb-transcoding/bulbtranscodingutils';

/**
 * Create progress bar element using {@link module:engine/view/uielement~UIElement}.
 *
 * @param {module:engine/view/downcastwriter~ViewDowncastWriter} writer
 * @param t
 * @param transcoding
 * @param instantPlay
 * @param assetType
 * @returns {{progressBarOutline: *, progressBar: *, progressBarBorder: *, progressBarMessage: *}}
 * @private
 */
export function createProgressBar(
  writer: ViewDowncastWriter,
  t: LocaleTranslate,
  transcoding,
  instantPlay,
  assetType,
) {
  let progressBar;
  let progressBarOutline;
  const assetName = assetType?.toUpperCase();
  if (instantPlay) {
    progressBar = writer.createUIElement('div', { class: 'ck-progress-bar instant-play-progress' });
    progressBarOutline = writer.createUIElement('div', { class: 'ck-progress-bar-outline instant-play-progress' });
  } else {
    progressBar = writer.createUIElement(
      'div',
      { class: transcoding ? 'ck-progress-bar' : 'ck-progress-bar upload-progress' },
    );
    progressBarOutline = writer.createUIElement(
      'div',
      { class: transcoding ? 'ck-progress-bar-outline' : 'ck-progress-bar-outline upload-progress' },
    );
  }
  const progressBarBorder = writer.createUIElement('div', { class: 'ck-progress-bar-border' });
  const progressBarMessage = writer.createContainerElement('div', { class: 'ck-progress-bar-message' });
  const processingText = writer.createText(t('transcoding_video_transcoding'));
  if (assetName !== 'AUDIO') {
    writer.insert(writer.createPositionAt(progressBarMessage, 0), processingText);
  }
  writer.setCustomProperty('progressBar', true, progressBar);
  writer.setCustomProperty('progressBarMessage', true, progressBarMessage);
  writer.setCustomProperty('progressBarBorder', true, progressBarBorder);
  writer.setCustomProperty('progressBarOutline', true, progressBarOutline);
  return {
    progressBar,
    progressBarOutline,
    progressBarBorder,
    progressBarMessage,
  };
}

/**
 * Initializes the progress bar on the transcoding asset.
 *
 * @param progressBarParam Contains all the information for progress bar initialization.
 * @returns module:engine/view/uielement~UIElement
 *
 */
export function initializeProgressBar(progressBarParam) {
  const writer = progressBarParam.viewWriter;
  const t = progressBarParam.translate;
  const { instantPlay } = progressBarParam;
  const { transcoding } = progressBarParam;
  const { assetType } = progressBarParam;
  const progressBarInfo = createProgressBar(writer, t, transcoding, instantPlay, assetType);
  if (transcoding) {
    writer.insert(writer.createPositionAt(progressBarParam.viewFigure, 'end'), progressBarInfo.progressBarMessage);
  }
  writer.insert(writer.createPositionAt(progressBarParam.viewFigure, 'end'), progressBarInfo.progressBar);
  writer.insert(writer.createPositionAt(progressBarParam.viewFigure, 'end'), progressBarInfo.progressBarOutline);
  writer.insert(writer.createPositionAt(progressBarParam.viewFigure, 'end'), progressBarInfo.progressBarBorder);

  // Set initial width to 0!
  progressBarParam.view.change((progressBarWriter) => {
    progressBarWriter.setStyle('width', `${progressBarParam.progress}%`, progressBarInfo.progressBar);
  });
  return progressBarInfo.progressBar;
}

/**
 * Create progress bar element for refreshing asset.
 * @param writer
 * @param t
 * @param blockId
 *
 * @returns {{refreshBarBorder: *, refreshBarMessage: *}}
 */
export function createRefreshBar(writer: ViewDowncastWriter, t: LocaleTranslate, blockId) {
  const refreshBarBorder = writer.createUIElement(
    'div',
    { class: 'ck-refresh-bar-border', refreshId: blockId },
    (domDocument) => this.toDomElement(domDocument),
  );
  const refreshBarMessage = writer.createUIElement(
    'div',
    { class: 'ck-refresh-bar-message', refreshMessageId: blockId },
    (domDocument) => {
      const domElement = this.toDomElement(domDocument);
      domElement.innerHTML = `<i class="fas fa-redo-alt" aria-hidden="true"></i>${t('transcoding_video_converted_refresh')}`;
      return domElement;
    },
  );
  writer.setCustomProperty('progressBar', true, refreshBarBorder);
  return {
    refreshBarBorder,
    refreshBarMessage,
  };
}

/**
 * Initializes the progress bar on the instantPlay asset.
 * @param viewFigure
 * @param writer
 * @param t
 */
export function refreshAsset(viewFigure, writer, t) {
  const blockId = viewFigure.getAttribute('bulb-block-id');
  const refreshBarInfo = createRefreshBar(writer, t, blockId);
  writer.insert(writer.createPositionAt(viewFigure, 'end'), refreshBarInfo.refreshBarMessage);
  writer.insert(writer.createPositionAt(viewFigure, 'end'), refreshBarInfo.refreshBarBorder);
}

/**
 * Updates the progress of the uploading asset by modifying the width style.
 *
 * @param view Current editing view.
 * @param progressBarUIElement progressBarUIElement that was created.
 * @param progress Current upload progress.
 *
 */
export function updateProgressBar(view, progressBarUIElement, progress) {
  // Update the progress bars' width:
  view.change((writer) => {
    writer.setStyle('width', `${progress}%`, progressBarUIElement);
  });
}

/**
 * Method which replaces instant play video with transcoded video.
 * @param assetObject Contains asset information.
 * @param modelElement Transcoding model element.
 * @param insertionPosition ViewPosition of asset.
 * @param videoElement instant play video element.
 */
export function refreshVideo(
  assetObject,
  modelElement: ModelElement,
  insertionPosition: ViewPosition,
  videoElement: ModelElement,
) {
  const editor = this;
  getUpdatedContentBlock(editor, assetObject, modelElement, insertionPosition);
  editor.model.change((writer) => {
    writer.remove(videoElement);
  });
}

/**
 * Removes {@link module:engine/view/uielement~UIElement} of given unique property from image figure element.
 *
 * @param {module:engine/view/element~ViewElement} viewFigure
 * @param {module:engine/view/downcastwriter~ViewDowncastWriter} writer
 * @param {String} uniqueProperty
 *
 * @private
 */
function removeUIElement(viewFigure, writer, uniqueProperty) {
  const element = getUIElement(viewFigure, uniqueProperty);

  if (element) {
    writer.remove(writer.createRangeOn(element));
  }
}

/**
 * Hides upload progress bar.
 *
 * @param {module:engine/view/containerelement~ContainerElement} viewFigure
 * @param {module:engine/view/downcastwriter~ViewDowncastWriter} writer
 *
 * @private
 */
export function hideProgressBar(viewFigure: ViewElement, writer: ViewDowncastWriter) {
  removeUIElement(viewFigure, writer, 'progressBar');
  // <bulb-changes>
  removeUIElement(viewFigure, writer, 'progressBarOutline');
  removeUIElement(viewFigure, writer, 'progressBarMessage');
  removeUIElement(viewFigure, writer, 'progressBarBorder');
}

/**
 * Removes placeholder together with infinite progress bar on given image figure.
 *
 * @param {module:engine/view/containerelement~ContainerElement} viewFigure
 * @param {module:engine/view/downcastwriter~ViewDowncastWriter} writer
 * @private
 */
export function hidePlaceholder(viewFigure: ViewElement, writer: ViewDowncastWriter) {
  if (viewFigure.hasClass('ck-image-upload-placeholder')) {
    writer.removeClass('ck-image-upload-placeholder', viewFigure);
  }
  removeUIElement(viewFigure, writer, 'placeholder');
}

/**
 * Adds ck-appear class to the image figure if one is not already applied.
 *
 * @param {module:engine/view/containerelement~ContainerElement} viewFigure
 * @param {module:engine/view/downcastwriter~ViewDowncastWriter} writer
 *
 * @private
 */
export function stopAppearEffect(viewFigure: ViewElement, writer: ViewDowncastWriter) {
  writer.removeClass('ck-appear', viewFigure);
}

/**
 * Helper method to clean up after the block is done uploading.
 */
export function clean(viewFigure: ViewElement, viewWriter: ViewDowncastWriter) {
  hideProgressBar(viewFigure, viewWriter);
  hidePlaceholder(viewFigure, viewWriter);
  stopAppearEffect(viewFigure, viewWriter);
}

// ######################## CKE METHODS THAT WE NEED ########################
// All of these methods were taken from cke's source file : imageuploadprogress.js.
// Any bulb customization is marked by <bulb-changes>...</bulb-changes> comments.

/**
 * @license Copyright (c) 2003-2020, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * Adds ck-appear class to the image figure if one is not already applied.
 *
 * @param {module:engine/view/containerelement~ContainerElement} viewFigure
 * @param {module:engine/view/downcastwriter~ViewDowncastWriter} writer
 *
 * @private
 */
export function startAppearEffect(viewFigure: ViewElement, writer: ViewDowncastWriter) {
  if (!viewFigure.hasClass('ck-appear')) {
    writer.addClass('ck-appear', viewFigure);
  }
}

/**
 * Returns {@link module:engine/view/uielement~UIElement} of given unique property from image figure element.
 * Returns `undefined` if element is not found.
 *
 * @param {module:engine/view/element~ViewElement} imageFigure
 * @param {String} uniqueProperty
 * @returns {module:engine/view/uielement~UIElement|undefined}
 *
 * @private
 */
export function getUIElement(imageFigure, uniqueProperty) {
  for (const child of imageFigure.getChildren()) {
    if (child.getCustomProperty(uniqueProperty)) {
      return child;
    }
  }
}
